import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { EnvironmentService } from 'src/app/core/services/environment/environment.service';
import { DataService } from 'src/app/core/services/data.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { Utils } from 'src/app/core/services/utils';
import { AuthData } from 'src/app/models/auth/auth-data.model';
import { AuthenticationUserCompany } from 'src/app/models/auth/authentication-response.model';
import { CacheService } from 'src/app/services/cache.service';

interface SamlHandoffAvailableScope {
  userCompanyGuid: string;
  userType: string;
  companyName: string;
  externalCompanyName?: string;
  roleName?: string;
}

interface SamlHandoffRedeemResponse {
  status: 'Success' | 'ScopeSelectionRequired';
  accessToken?: string;
  refreshToken?: string;
  scope?: string;
  returnUrl?: string;
  code?: string;
  availableScopes?: SamlHandoffAvailableScope[];
  userName?: string;
  userCompanyGuid?: string;
  companyGuid?: string;
  availableCompanies?: AuthenticationUserCompany[];
}

@Component({
    selector: 'saml-callback',
    templateUrl: 'saml-callback.component.html',
    standalone: false
})
export class SamlCallbackComponent implements OnInit, OnDestroy {

  loading = true;
  errorMessage: string | undefined;
  availableScopes: SamlHandoffAvailableScope[] = [];
  selectingScope = false;

  private _code: string | undefined;
  private _querySub: Subscription | undefined;

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _dataService: DataService,
    private readonly _environment: EnvironmentService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _cacheService: CacheService
  ) { }

  ngOnInit(): void {
    this._querySub = this._activatedRoute.queryParams.subscribe((queryParams) => {
      const code = queryParams['code'];
      // Strip the code from the address bar immediately so it never lingers in history/referrer.
      history.replaceState({}, '', window.location.pathname);

      if (!code) {
        this.redirectToLoginWithError();
        return;
      }

      this._code = code;
      this.redeem(code);
    });
  }

  ngOnDestroy(): void {
    this._querySub?.unsubscribe();
  }

  onScopeSelected(scope: SamlHandoffAvailableScope): void {
    if (!this._code || this.selectingScope) {
      return;
    }
    this.selectingScope = true;
    this.redeem(this._code, scope.userCompanyGuid);
  }

  /**
   * Every option belongs to the company that authenticated the assertion, so the company name alone cannot
   * tell two rows apart - the external (TPO) company and the role are what distinguish them.
   */
  scopeLabel(scope: SamlHandoffAvailableScope): string {
    const qualifier = scope.externalCompanyName || scope.companyName;
    const role = scope.roleName || scope.userType;
    return qualifier ? `${role} - ${qualifier}` : role;
  }

  backToLogin(): void {
    window.location.href = `${window.location.protocol}//${window.location.host}/login`;
  }

  private redeem(code: string, userCompanyGuid?: string): void {
    this.loading = true;
    this.errorMessage = undefined;

    const url = this._environment.apiInfo.apiBaseUrl + 'api/auth/saml-sso/redeem';
    const body: { code: string; userCompanyGuid?: string } = { code };
    if (userCompanyGuid) {
      body.userCompanyGuid = userCompanyGuid;
    }

    this._dataService.postWithoutAuth(url, body, false).subscribe({
      next: (response: SamlHandoffRedeemResponse) => {
        if (response.status === 'ScopeSelectionRequired') {
          this._code = response.code;
          this.availableScopes = response.availableScopes ?? [];
          this.loading = false;
          this.selectingScope = false;

          if (!this.availableScopes.length) {
            this.errorMessage = 'Single sign-on succeeded, but there are no accounts available to sign into. Contact your administrator.';
          }
          return;
        }

        if (response.status === 'Success' && response.accessToken && response.scope) {
          if (!this.persistAuthData(response)) {
            this.redirectToLoginWithError();
            return;
          }
          this.navigateAfterLogin(response.returnUrl, response.scope);
          return;
        }

        this.redirectToLoginWithError();
      },
      error: (err: HttpErrorResponse) => {
        // The API reports a real reason (e.g. the asserted user has no account in this company) as problem
        // details; show it here rather than bouncing to /login, which loses the reason entirely.
        const detail = err.error?.detail;
        if (detail) {
          this.errorMessage = detail;
          this.availableScopes = [];
          this.loading = false;
          this.selectingScope = false;
          return;
        }

        this.redirectToLoginWithError();
      }
    });
  }

  /**
   * Writes the same session shape a password login writes, through the same chokepoint
   * (`LocalStorageService.authorizationData` + `CacheService.setTenantPrefix`), so token refresh,
   * the company switcher and the cross-tab session-integrity guard behave identically for SAML users.
   * Returns false when identity fields or the token can't be resolved, so the caller fails into the
   * login page instead of persisting a half-formed session / undefined tenant prefix.
   */
  private persistAuthData(response: SamlHandoffRedeemResponse): boolean {
    const token = response.accessToken;

    // Utils.parseJwt throws on an empty/malformed token rather than returning null.
    let jwtPayload: any;
    try {
      jwtPayload = Utils.parseJwt(token);
    } catch {
      return false;
    }

    if (!jwtPayload || !jwtPayload.exp) {
      return false;
    }

    // Redeem should always return userCompanyGuid on Success; JWT nameidentifier/sub is the same value
    // (JwtBuilder writes UserCompanyGuid there) and is only a defense-in-depth fallback.
    const userCompanyGuid = response.userCompanyGuid
      || jwtPayload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']
      || jwtPayload.sub;
    const companyGuid = response.companyGuid ?? jwtPayload.CompanyGuid;

    if (!userCompanyGuid || !companyGuid || !response.refreshToken) {
      return false;
    }

    const authData: AuthData = {
      companyGuid,
      userCompanyGuid,
      token,
      role: jwtPayload.AspNetRole,
      userName: response.userName,
      refreshToken: response.refreshToken,
      companies: response.availableCompanies ?? [],
      rememberMe: false,
      expiresAt: Utils.getTokenExpireDate(jwtPayload.exp)
    };

    this._cacheService.setTenantPrefix(authData.userCompanyGuid);
    this._localStorageService.authorizationData = authData;

    // Mirrors the password-login path: clear the TV-auth session flag so notification prompts work.
    try {
      localStorage.removeItem('lodasoft_tv_auth_session');
    } catch {
      // localStorage may not be available
    }

    return true;
  }

  private navigateAfterLogin(returnUrl: string | undefined, scope: string): void {
    window.location.href = this.resolvePostLoginUrl(returnUrl, scope);
  }

  /**
   * Resolves the post-login destination without treating a bare path as relative to /saml-callback.
   * The API already absolutizes root-relative returnUrls; this mirrors that on the client so a relative
   * DefaultRedirectUrl (or any non-absolute value) cannot land the user on /saml-callback/<path>.
   */
  private resolvePostLoginUrl(returnUrl: string | undefined, scope: string): string {
    const origin = `${window.location.protocol}//${window.location.host}`;
    const dashboardPath = scope === 'TPO' ? '/tpo/dashboard' : '/admin/dashboardv2';
    const dashboardUrl = `${origin}${dashboardPath}`;

    if (!returnUrl?.trim()) {
      return dashboardUrl;
    }

    const trimmed = returnUrl.trim();

    // Absolute http(s): only follow when same-origin as this portal (ACS already enforces this server-side).
    if (this.isAbsoluteHttpUrl(trimmed)) {
      try {
        if (new URL(trimmed).origin === window.location.origin) {
          return trimmed;
        }
      } catch {
        // fall through to dashboard
      }
      return dashboardUrl;
    }

    // Root-relative path (/admin/...), not protocol-relative (//host/...).
    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
      return `${origin}${trimmed}`;
    }

    // Path-relative / unrecognized — never resolve against /saml-callback.
    return dashboardUrl;
  }

  private isAbsoluteHttpUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private redirectToLoginWithError(): void {
    window.location.href = `${window.location.protocol}//${window.location.host}/login?samlError=redeem_failed`;
  }
}
